import { useState } from "react";
import { Link } from "react-router-dom";
import { User, MessageSquare, Star, Lock, CreditCard, Shield, ChevronDown } from "lucide-react";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";

export default function HowItWorks() {
  const [role, setRole] = useState('borrower');
  const [openFaq, setOpenFaq] = useState(null);

  const seoData = {
    title: "How It Works | Paradise Borrowed",
    description: "Learn how to borrow gear for your next adventure or earn money lending your items. Simple, secure peer-to-peer borrowing with email-verified users and secure payments.",
    url: "https://www.paradiseborrowed.com/how-it-works"
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What if an item gets damaged?",
      answer: <>If damage occurs, lenders have 48 hours after return confirmation to file a dispute. Submit photos and evidence through your dashboard. Our Trust &amp; Safety team reviews all claims and issues a written determination within 5 business days. If the claim is approved, we'll work with both parties to resolve the damage cost fairly. Users found to have provided false information in a dispute are permanently banned. See our full <Link to="/trust" className="text-coral hover:underline">Dispute Resolution Policy</Link> for details. <strong>Note:</strong> Lenders are responsible for maintaining appropriate insurance coverage for their items. Paradise Borrowed does not provide insurance.</>
    },
    {
      question: "How do payments work?",
      answer: <>All payments are processed securely through Stripe. We take 7% from each side — a 7% lender fee deducted from the lender's payout, and a 7% borrower service fee (minimum $2) added at checkout. The full breakdown is shown to both sides before any payment goes through. See our <Link to="/fees" className="text-coral hover:underline">Fees page</Link> for a full breakdown.</>
    },
    {
      question: "Where can I lend or borrow?",
      answer: "Paradise Borrowed currently supports select cities across 18 US states — including the Northeast corridor, Southeast, Florida, Ohio, California, and Washington State — as well as eight high-income Caribbean territories including the Cayman Islands, Turks and Caicos, British Virgin Islands, Bermuda, Aruba, Barbados, the Bahamas, and Saint Barthélemy. A full list of supported locations is available on the platform."
    },
    {
      question: "How do I know if a borrower or lender is trustworthy?",
      answer: "Check their profile for reviews from past transactions. Users with positive reviews and completed borrowing history indicate a proven track record. When in doubt, message them through the platform first — all messages are logged and timestamped for your protection."
    },
    {
      question: "What items can I list?",
      answer: <>Almost anything — from beach gear and baby equipment to cameras, tools, and sporting goods. Items must be legal, safe, accurately described, and in the condition you claim. We do not allow firearms or weapons of any kind, controlled substances, live animals, stolen or counterfeit items, or anything whose lending is prohibited by applicable law. See our <Link to="/terms-of-service" className="text-coral hover:underline">Terms of Service</Link> for the complete prohibited items list.</>
    }
  ];

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonicalUrl={seoData.url}
        type="website"
        schema={generateWebPageSchema(seoData)}
      />
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="how-it-works-page">
        
        {/* Hero */}
        <div className="text-center pb-12 mb-12 border-b border-stone-200">
          <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">Platform guide</p>
          <h1 className="text-4xl sm:text-5xl text-stone-900 mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
            How Paradise<br/>
            <span className="text-coral" style={{ fontFamily: "'DM Serif Display', serif", fontStyle: 'italic' }}>Borrowed</span> works
          </h1>
          <p className="text-stone-500 max-w-xl mx-auto leading-relaxed">
            Whether you're looking to borrow gear for your next adventure or earn money lending your items, we've made it simple and secure.
          </p>
        </div>

        {/* Role Toggle */}
        <div className="flex justify-center gap-3 mb-12">
          <button
            onClick={() => setRole('borrower')}
            className={`px-7 py-2.5 rounded-lg border text-sm transition-all ${role === 'borrower' ? 'bg-coral text-white border-coral' : 'bg-white text-stone-600 border-stone-300 hover:border-coral hover:text-coral'}`}
          >
            I want to borrow
          </button>
          <button
            onClick={() => setRole('lender')}
            className={`px-7 py-2.5 rounded-lg border text-sm transition-all ${role === 'lender' ? 'bg-coral text-white border-coral' : 'bg-white text-stone-600 border-stone-300 hover:border-coral hover:text-coral'}`}
          >
            I want to lend
          </button>
        </div>

        {/* Borrower Panel */}
        {role === 'borrower' && (
          <div>
            <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">For borrowers</p>
            
            <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
              <p className="text-xs tracking-wider uppercase text-stone-400 mb-2">How to borrow</p>
              <h2 className="text-2xl sm:text-3xl text-stone-900 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Get the gear you need,<br/>without buying or storing it.
              </h2>
              <p className="text-stone-500 max-w-xl leading-relaxed">
                Perfect for travelers, vacationers, and anyone who needs something once without the commitment of owning it.
              </p>
            </div>

            <div className="bg-stone-100 rounded-2xl p-6 mb-12">
              <p className="font-medium text-stone-900 pb-4 mb-6 border-b border-stone-200">Four steps from search to adventure</p>
              <div className="space-y-0">
                <Step num="1" title="Choose your destination" desc="Search by location to find items available where you're going — Caribbean islands, US cities, or your own neighborhood. Browse listings in your target area before you even arrive." showLine />
                <Step num="2" title="Browse & compare" desc="Explore available items, read verified reviews from other borrowers, and check photos to find exactly what you need. Every listing includes the item's condition rating and lender profile." showLine />
                <Step num="3" title="Connect with the lender" desc="Message the lender directly through our platform to ask questions, confirm availability, and arrange pickup or delivery. All communication is logged and timestamped for your protection." showLine />
                <Step num="4" title="Borrow, enjoy, return" desc="Pick up or receive your item, use it for your adventure, and return it in the same condition when you're done. Confirm the return through the app — that's it." />
              </div>
            </div>
          </div>
        )}

        {/* Lender Panel */}
        {role === 'lender' && (
          <div>
            <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">For lenders</p>
            
            <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
              <p className="text-xs tracking-wider uppercase text-stone-400 mb-2">How to lend</p>
              <h2 className="text-2xl sm:text-3xl text-stone-900 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Turn idle items into<br/>steady income.
              </h2>
              <p className="text-stone-500 max-w-xl leading-relaxed">
                Your surfboard, camera, or baby gear could be exactly what someone needs. List once and earn every time it goes out.
              </p>
            </div>

            <div className="bg-stone-100 rounded-2xl p-6 mb-12">
              <p className="font-medium text-stone-900 pb-4 mb-6 border-b border-stone-200">Four steps from listing to earning</p>
              <div className="space-y-0">
                <Step num="1" title="List your item" desc="Take a few photos, write a clear description, set your daily price, and specify your location. Rate your item's condition honestly — accurate listings earn better reviews. Takes just minutes to go live." showLine />
                <Step num="2" title="Set your terms" desc="Choose whether you'll deliver, allow pickup, or both. Set your availability calendar and add any special requirements upfront." showLine />
                <Step num="3" title="Respond to requests" desc="When borrowers reach out, review their request and profile. Check their verified reviews and ratings from past bookings. Accept or decline — you are always in control of who borrows your items." showLine />
                <Step num="4" title="Earn & build your reputation" desc="Complete the booking, get paid, and earn a review. Strong reviews lead to more bookings and higher earnings. Lenders with great track records attract more requests and can command premium rates." />
              </div>
            </div>
          </div>
        )}

        {/* Trust & Safety */}
        <div className="mb-12">
          <div className="bg-white border border-stone-200 rounded-t-2xl p-6">
            <p className="text-xs tracking-wider uppercase text-stone-400 mb-2">Trust & Safety</p>
            <h2 className="text-2xl sm:text-3xl text-stone-900 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Why trust Paradise Borrowed?
            </h2>
            <p className="text-stone-500 max-w-xl leading-relaxed">
              We've built safeguards to protect both borrowers and lenders, ensuring a smooth experience for everyone on the platform.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border border-t-0 border-stone-200 rounded-b-2xl overflow-hidden">
            <TrustCard icon={<User className="w-4 h-4 text-coral" />} title="Verified profiles" desc="Every user has an email-verified profile with reviews and ratings from past transactions. See who you're dealing with before you commit." />
            <TrustCard icon={<MessageSquare className="w-4 h-4 text-coral" />} title="Secure messaging" desc="All communication happens through our platform. Every message is timestamped and logged — keeping records of all agreements in one protected place." />
            <TrustCard icon={<Star className="w-4 h-4 text-coral" />} title="Review system" desc="Both borrowers and lenders leave verified reviews after each completed transaction. Good behavior is rewarded; bad actors are identified and removed." />
            <TrustCard icon={<Lock className="w-4 h-4 text-coral" />} title="Privacy protected" desc="Your personal contact information is never shared until you choose to share it. Control what others can see on your profile at all times." />
            <TrustCard icon={<CreditCard className="w-4 h-4 text-coral" />} title="Secure payments" desc="All transactions are processed securely through Stripe. We take a simple 7% fee from each side — clearly shown before you confirm." />
            <TrustCard icon={<Shield className="w-4 h-4 text-coral" />} title="Community standards" desc="We have clear guidelines for behavior. Users who violate our standards — including submitting false dispute claims — are permanently removed from the platform." />
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-12">
          <p className="text-xs tracking-widest uppercase text-stone-400 mb-2">Common questions</p>
          <h2 className="text-2xl sm:text-3xl text-stone-900 mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Things people ask
          </h2>
          <div className="border border-stone-200 rounded-2xl overflow-hidden bg-white">
            {faqs.map((faq, index) => (
              <div key={index} className={`border-b border-stone-200 last:border-b-0`}>
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-stone-50 transition-colors"
                >
                  <span className="font-medium text-sm text-stone-900">{faq.question}</span>
                  <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${openFaq === index ? 'max-h-48' : 'max-h-0'}`}>
                  <div className="px-5 pb-5 text-sm text-stone-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-coral rounded-2xl p-8 sm:p-12 text-center mb-12">
          <h2 className="text-2xl sm:text-3xl text-white mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Ready to get started?
          </h2>
          <p className="text-white/80 mb-6 leading-relaxed">
            Join people already borrowing and lending on Paradise Borrowed.<br className="hidden sm:block" />
            Create your free account in minutes.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/auth" className="px-7 py-3 bg-white text-coral font-medium rounded-lg text-sm hover:opacity-90 transition-opacity">
              Create a free account
            </Link>
            <Link to="/browse" className="px-7 py-3 bg-white/15 text-white border border-white/40 font-medium rounded-lg text-sm hover:bg-white/25 transition-colors">
              Browse listings
            </Link>
          </div>
        </div>

        {/* Legal Note */}
        <div className="border-t border-stone-200 pt-6">
          <p className="text-xs text-stone-400 leading-relaxed">
            Use of the platform is subject to our <Link to="/terms-of-service" className="text-coral hover:underline">Terms of Service</Link>, <Link to="/trust" className="text-coral hover:underline">Dispute Resolution Policy</Link>, and <Link to="/privacy-policy" className="text-coral hover:underline">Privacy Policy</Link>. Paradise Borrowed, Inc. is a Delaware corporation and operates as a neutral peer-to-peer marketplace. We are not a party to any borrowing agreement between users. Platform availability is limited to supported US states and Caribbean territories. For questions: <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a>.
          </p>
        </div>
      </div>
    </>
  );
}

function Step({ num, title, desc, showLine = false }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-coral text-white flex items-center justify-center text-sm font-medium shrink-0">
          {num}
        </div>
        {showLine && <div className="w-px flex-1 bg-stone-300 my-1.5"></div>}
      </div>
      <div className={showLine ? "pb-6" : ""}>
        <p className="font-medium text-stone-900 mb-1">{title}</p>
        <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function TrustCard({ icon, title, desc }) {
  return (
    <div className="bg-white p-5 border-r border-t border-stone-200 last:border-r-0 [&:nth-child(3n)]:border-r-0 [&:nth-child(-n+3)]:border-t-0 sm:[&:nth-child(-n+2)]:border-t-0 lg:[&:nth-child(-n+3)]:border-t-0">
      <div className="w-9 h-9 rounded-lg bg-coral-light flex items-center justify-center mb-3">
        {icon}
      </div>
      <p className="font-medium text-sm text-stone-900 mb-1">{title}</p>
      <p className="text-xs text-stone-500 leading-relaxed">{desc}</p>
    </div>
  );
}
